// ── Overview Mini-Player ────────────────────────────────────────
// Persistent mini-player for full paper overview audio with chapter markers.
// Stays visible at the bottom of LearnJourneyView while navigating concepts.

import React, { useState, useRef, useEffect, useCallback } from 'react';

const SPEEDS = [0.75, 1.0, 1.25, 1.5, 2.0];

function formatTime(sec) {
  if (!sec || !isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function OverviewMiniPlayer({
  chapters,       // [{ id, title, concepts, audioBase64, mimeType, wordCount }]
  overviewTitle,  // e.g. "Deep Dive: Paper Title"
  onClose,
  onSendToTelegram, // optional: click handler for the "Send to Telegram" button
}) {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);  // 0..1
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(1); // 1.0x
  const [expanded, setExpanded] = useState(false); // show chapter list
  const audioRef = useRef(null);
  const urlsRef = useRef([]);

  // Create object URLs for all chapters on mount
  useEffect(() => {
    const urls = chapters.map(ch => {
      const binary = atob(ch.audioBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: ch.mimeType || 'audio/mpeg' });
      return URL.createObjectURL(blob);
    });
    urlsRef.current = urls;
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [chapters]);

  // Load audio for current chapter
  useEffect(() => {
    if (!urlsRef.current[currentChapter]) return;
    const audio = new Audio(urlsRef.current[currentChapter]);
    audio.playbackRate = SPEEDS[speedIdx];
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration) setProgress(audio.currentTime / audio.duration);
    });
    audio.addEventListener('ended', () => {
      // Auto-advance to next chapter
      if (currentChapter < chapters.length - 1) {
        setCurrentChapter(prev => prev + 1);
        setIsPlaying(true);
      } else {
        setIsPlaying(false);
        setProgress(1);
      }
    });

    if (isPlaying) audio.play().catch(() => {});

    return () => {
      audio.pause();
      audio.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChapter, chapters.length]);

  // Sync play state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.play().catch(() => {});
    else audio.pause();
  }, [isPlaying]);

  // Sync speed
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[speedIdx];
  }, [speedIdx]);

  const togglePlay = () => setIsPlaying(p => !p);

  const cycleSpeed = () => setSpeedIdx(i => (i + 1) % SPEEDS.length);

  const seek = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = pct * audioRef.current.duration;
      setProgress(pct);
    }
  }, []);

  const skip = (sec) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + sec));
    }
  };

  const jumpToChapter = (idx) => {
    setCurrentChapter(idx);
    setIsPlaying(true);
    setExpanded(false);
  };

  const chapter = chapters[currentChapter];

  return (
    <div className="overview-mini-player">
      {/* Expanded chapter list */}
      {expanded && (
        <div className="overview-chapters-panel">
          <div className="overview-chapters-header">
            <span>Chapters</span>
            <button className="overview-chapters-close" onClick={() => setExpanded(false)}>✕</button>
          </div>
          <div className="overview-chapters-list">
            {chapters.map((ch, i) => (
              <button
                key={ch.id || i}
                className={`overview-chapter-item ${i === currentChapter ? 'active' : ''}`}
                onClick={() => jumpToChapter(i)}
              >
                <span className="overview-chapter-num">{i + 1}</span>
                <span className="overview-chapter-title">{ch.title}</span>
                {i === currentChapter && isPlaying && <span className="overview-chapter-playing">♪</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mini player bar */}
      <div className="overview-mini-bar">
        {/* Chapter nav / expand */}
        <button className="overview-mini-chapters-btn" onClick={() => setExpanded(e => !e)} title="Chapters">
          <span className="overview-mini-ch-num">{currentChapter + 1}/{chapters.length}</span>
        </button>

        {/* Play controls */}
        <button className="learn-audio-skip" onClick={() => skip(-15)} title="-15s">⟲15</button>
        <button className="overview-mini-play" onClick={togglePlay}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button className="learn-audio-skip" onClick={() => skip(15)} title="+15s">15⟳</button>

        {/* Chapter title + progress */}
        <div className="overview-mini-info">
          <span className="overview-mini-title">{chapter?.title || overviewTitle}</span>
          <div className="overview-mini-progress-wrap" onClick={seek}>
            <div className="overview-mini-progress-bar">
              <div className="overview-mini-progress-fill" style={{ width: `${progress * 100}%` }} />
              {/* Chapter markers */}
              {chapters.length > 1 && chapters.map((_, i) => {
                if (i === 0) return null;
                const markerPos = (i / chapters.length) * 100;
                return <div key={i} className="overview-mini-chapter-marker" style={{ left: `${markerPos}%` }} />;
              })}
            </div>
          </div>
        </div>

        {/* Time + speed */}
        <span className="learn-audio-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
        <button className="learn-audio-speed" onClick={cycleSpeed}>{SPEEDS[speedIdx]}×</button>

        {/* Send to Telegram */}
        {onSendToTelegram && (
          <button
            className="overview-mini-telegram"
            onClick={onSendToTelegram}
            title="Send to Telegram"
            aria-label="Send overview to Telegram"
          >
            ✈ Telegram
          </button>
        )}

        {/* Close */}
        <button className="overview-mini-close" onClick={onClose} title="Close player" aria-label="Close player">✕</button>
      </div>
    </div>
  );
}
